import React, { useEffect } from 'react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { DialogBody, DialogContent, DialogRoot } from '@components/ui/dialog';
import { PostRaceState } from '@services/postRaceService';
import { formatLongDate } from '@utils/v2/weekDates';
import { formatDuration } from '@blocks/sessionValidation/utils';

interface Props {
  state: PostRaceState;
  onNewRace: () => void;
  onNoObjective: () => void;
  onPostpone: () => void;
  busy: boolean;
  onShown: () => void;
}

const ChoiceCard: React.FC<{ emoji: string; title: string; description: string; onClick: () => void; disabled: boolean }> = ({
  emoji,
  title,
  description,
  onClick,
  disabled,
}) => (
  <Box
    as="button"
    textAlign="left"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="xl"
    p={4}
    bg="white"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    opacity={disabled ? 0.6 : 1}
    _hover={disabled ? undefined : { borderColor: 'blue.400', bg: 'blue.50' }}
    onClick={disabled ? undefined : onClick}
  >
    <HStack gap={3} align="start">
      <Text fontSize="2xl">{emoji}</Text>
      <VStack align="start" gap={0} flex={1}>
        <Text fontWeight="semibold">{title}</Text>
        <Text fontSize="sm" color="gray.500">{description}</Text>
      </VStack>
    </HStack>
  </Box>
);

/**
 * PRD 8 — « Et maintenant ? ». Mandatory: no close trigger, no escape, no click-away. The only ways
 * out are a choice or « Me le rappeler plus tard » (F8), which reopens it at the next app opening but
 * not the same day.
 *
 * In both choices the recovery stays intact (R33), which the engine guarantees through `earliestStart`
 * — nothing about it is decided here.
 *
 * R47 §6.3: the same screen closes a **reprise** plan (`kind = 'PLAN_END'`). It then uses the
 * degraded variant of §5 *without* the « résultat non renseigné » message — there was no race to
 * report — and the athlete slides onto the open generalist the next day if he chooses nothing.
 */
export const PostRaceDialog: React.FC<Props> = ({ state, onNewRace, onNoObjective, onPostpone, busy, onShown }) => {
  useEffect(() => {
    onShown();
    // Fired once per mount: `onShown` is idempotent server-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { kind, race, result, preparation, recovery, planEndsOn } = state;
  const planEnd = kind === 'PLAN_END';

  return (
    <DialogRoot open placement="center" closeOnInteractOutside={false} closeOnEscape={false}>
      <DialogContent borderRadius="2xl" maxW="480px" mx={4}>
        <DialogBody p={8}>
          <VStack gap={5} align="stretch">
            <VStack gap={2} align="center">
              <Box w="72px" h="72px" bg="orange.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="3xl">
                {planEnd ? '🌱' : '🏆'}
              </Box>
              <Text fontWeight="bold" fontSize="xl" textAlign="center">
                {planEnd ? 'Bravo, ton plan de reprise est terminé !' : race?.name ? `Bravo, ${race.name} !` : 'Bravo, course terminée !'}
              </Text>
              {(planEnd ? planEndsOn : race?.date) && (
                <Text fontSize="sm" color="gray.500">{formatLongDate((planEnd ? planEndsOn : race?.date) as string)}</Text>
              )}
            </VStack>

            {planEnd ? (
              <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
                <HStack justify="space-around" align="start">
                  <VStack gap={0}>
                    <Text fontSize="xl" fontWeight="bold">{preparation.completedSessions}</Text>
                    <Text fontSize="xs" color="gray.500">séances validées</Text>
                  </VStack>
                  <VStack gap={0}>
                    <Text fontSize="xl" fontWeight="bold">{preparation.weeks}</Text>
                    <Text fontSize="xs" color="gray.500">semaines de reprise</Text>
                  </VStack>
                </HStack>
              </Box>
            ) : result ? (
              <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
                <HStack justify="space-around" align="start">
                  <VStack gap={0}>
                    <Text fontSize="xl" fontWeight="bold">{formatDuration(result.totalTimeSeconds)}</Text>
                    <Text fontSize="xs" color="gray.500">temps total</Text>
                  </VStack>
                  <VStack gap={0}>
                    <Text fontSize="xl" fontWeight="bold">{preparation.completedSessions}</Text>
                    <Text fontSize="xs" color="gray.500">séances validées</Text>
                  </VStack>
                  <VStack gap={0}>
                    <Text fontSize="xl" fontWeight="bold">{preparation.weeks}</Text>
                    <Text fontSize="xs" color="gray.500">semaines de prépa</Text>
                  </VStack>
                </HStack>
              </Box>
            ) : (
              /* §5 degraded: the figures go, the choices stay. A missing result must never trap the athlete. */
              <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} bg="gray.50">
                <Text fontSize="sm" color="gray.600">Tu n’as pas encore renseigné ton résultat de compétition.</Text>
              </Box>
            )}

            {/* F3: the end of the recovery and the day training resumes are two different dates. */}
            {recovery && (
              <Text fontSize="sm" color="gray.600">
                Ta récupération se termine le <strong>{formatLongDate(recovery.endsOn)}</strong>. Ton entraînement
                reprend dès le <strong>{formatLongDate(recovery.trainingResumesOn)}</strong>.
              </Text>
            )}

            <VStack gap={3} align="stretch">
              <Text fontWeight="bold">Et maintenant ?</Text>
              <ChoiceCard
                emoji="🎯"
                title="Une nouvelle course"
                description={
                  planEnd
                    ? 'Choisis ta prochaine cible ; ta préparation démarrera dès maintenant.'
                    : 'Choisis ta prochaine cible ; ta préparation démarrera après ta récupération.'
                }
                onClick={onNewRace}
                disabled={busy}
              />
              <ChoiceCard
                emoji="🌱"
                title="Sans objectif daté"
                description="Tu continues sur ton plan de fond, sans course cible. Rien à créer."
                onClick={onNoObjective}
                disabled={busy}
              />
            </VStack>

            <Button variant="ghost" size="sm" onClick={onPostpone} disabled={busy}>
              Me le rappeler plus tard
            </Button>
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default PostRaceDialog;
